import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../../components/ConfirmModal';
import {api} from '../../api';


const STAGE_COLORS = {
  DISCOVERY: 'bg-purple-100 text-purple-800',
  QUALIFIED: 'bg-blue-100 text-blue-800',
  PROPOSAL: 'bg-yellow-100 text-yellow-800',
  NEGOTIATION: 'bg-orange-100 text-orange-800',
  CLOSED_WON: 'bg-green-100 text-green-800',
  CLOSED_LOST: 'bg-red-100 text-red-800',
};
const PRIORITY_COLORS = { HIGH: 'bg-red-100 text-red-800', MEDIUM: 'bg-yellow-100 text-yellow-800', LOW: 'bg-green-100 text-green-800' };
const STATUS_COLORS = { TODO: 'bg-gray-100 text-gray-800', IN_PROGRESS: 'bg-blue-100 text-blue-800', DONE: 'bg-green-100 text-green-800' };
const ACTIVITY_TYPES = ['CALL', 'EMAIL', 'MEETING', 'LINKEDIN_MESSAGE'];

function ActivityForm({ dealContacts, initial, onSubmit, onCancel, submitLabel }) {
  const [form, setForm] = useState(initial);

  const toggleContact = (contactId) => {
    const ids = form.contactIds || [];
    setForm({ ...form, contactIds: ids.includes(contactId) ? ids.filter(c => c !== contactId) : [...ids, contactId] });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.subject.trim()) return;
    const payload = { ...form };
    if (payload.activityDate) {
      payload.activityDate = new Date(payload.activityDate).toISOString();
    }
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="mb-4 space-y-3 border-b pb-4">
      <div className="flex gap-2">
        <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600">
          {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').charAt(0) + t.replace(/_/g, ' ').slice(1).toLowerCase()}</option>)}
        </select>
        <input type="text" placeholder="Subject *" required value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
      </div>
      <div className="flex gap-2">
        <input type="datetime-local" value={form.activityDate} onChange={(e) => setForm({...form, activityDate: e.target.value})} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
      </div>
      <textarea placeholder="Details (optional)" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
      {dealContacts.length > 0 && (
        <div>
          <p className="text-sm text-gray-500 mb-1">With</p>
          <div className="flex flex-wrap gap-2">
            {dealContacts.map(c => {
              const selected = (form.contactIds || []).includes(c.id);
              return (
                <button key={c.id} type="button" onClick={() => toggleContact(c.id)}
                  className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                    selected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}>
                  {c.firstName} {c.lastName}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        {onCancel && <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>}
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{submitLabel}</button>
      </div>
    </form>
  );
}

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [activities, setActivities] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [deleteModal, setDeleteModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState(null);

  const loadDeal = () => api(`/deals/${id}`).then(r => r.json()).then(setDeal);
  const loadActivities = () => api(`/crm-activities?dealId=${id}`).then(r => r.json()).then(setActivities);

  const loadEstimates = () => api('/estimates').then(r => r.ok ? r.json() : []).then(all => setEstimates(all.filter(e => e.dealId === id)));

  useEffect(() => {
    loadDeal();
    loadActivities();
    loadEstimates();
    api(`/crm-tasks?dealId=${id}`).then(r => r.json()).then(setTasks);
    api(`/tenant-users`).then(r => r.json()).then(users => {
      const map = {};
      (users || []).forEach(u => { map[u.userId] = `${u.firstName} ${u.lastName}`.trim() || u.username; });
      setUserMap(map);
    }).catch(() => {});
  }, [id]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    const body = {
      name: deal.name, companyId: deal.companyId, value: deal.value, stage: deal.stage,
      contactIds: (deal.contacts || []).map(c => c.id), closeDate: deal.closeDate,
      tagIds: (deal.tags || []).map(t => t.id),
      notes: [...(deal.notes || []), { body: newNote.trim() }]
    };
    const res = await api(`/deals/${id}`, { method: 'PUT', body: JSON.stringify(body) });
    if (res.ok) { setNewNote(''); loadDeal(); }
  };

  const handleAddActivity = async (form) => {
    const res = await api(`/crm-activities`, { method: 'POST', body: JSON.stringify({ ...form, dealId: id }) });
    if (res.ok) { setShowActivityForm(false); loadActivities(); }
  };

  const handleUpdateActivity = async (form) => {
    const res = await api(`/crm-activities/${editingActivityId}`, { method: 'PUT', body: JSON.stringify({ ...form, dealId: id }) });
    if (res.ok) { setEditingActivityId(null); loadActivities(); }
  };

  const handleDelete = async () => {
    await api(`/deals/${id}`, { method: 'DELETE' });
    navigate('/crm/deals');
  };

  if (!deal) return null;

  const dealContacts = deal.contacts || [];

  return (
    <Layout>
      <Link to="/crm/deals" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">&larr; Back to Deals</Link>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{deal.name}</h1>
              {deal.companyId && <p className="text-xl text-gray-600"><Link to={`/crm/companies/${deal.companyId}`} className="text-blue-600 hover:text-blue-800">{deal.companyName}</Link></p>}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${STAGE_COLORS[deal.stage] || 'bg-gray-100 text-gray-800'}`}>
                {deal.stage.replace(/_/g, ' ')}
              </span>
              {deal.tags && deal.tags.length > 0 && deal.tags.map(t => (
                <span key={t.id} className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">{t.name}</span>
              ))}
              <Link to={`/crm/deals/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Edit</Link>
              <button onClick={() => setDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-blue-50">Delete</button>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">Value</p>
            <p className="text-gray-900 font-medium">{deal.value != null ? `$${deal.value.toLocaleString()}` : '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Close Date</p>
            <p className="text-gray-900 font-medium">{deal.closeDate || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Contacts</p>
            <p className="text-gray-900 font-medium">
              {dealContacts.length > 0
                ? dealContacts.map((c, i) => (
                    <span key={c.id}>
                      {i > 0 && ', '}
                      <Link to={`/crm/contacts/${c.id}`} className="text-blue-600 hover:text-blue-800">{c.firstName} {c.lastName}</Link>
                    </span>
                  ))
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Created</p>
            <p className="text-gray-900 font-medium">{new Date(deal.createdAt).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Activity */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Activity</h2>
          <button onClick={() => { setShowActivityForm(!showActivityForm); setEditingActivityId(null); }} className="text-sm text-blue-600 hover:text-blue-800">
            {showActivityForm ? 'Cancel' : '+ Log activity'}
          </button>
        </div>
        <div className="p-4 md:p-6">
          {showActivityForm && (
            <ActivityForm
              dealContacts={dealContacts}
              initial={{ type: 'CALL', subject: '', body: '', activityDate: new Date().toISOString().slice(0, 16), contactIds: [] }}
              onSubmit={handleAddActivity}
              onCancel={() => setShowActivityForm(false)}
              submitLabel="Log"
            />
          )}
          {activities.length > 0 ? (
            <div className="space-y-4">
              {activities.map((a) => (
                <div key={a.id} className="border-b pb-3 last:border-b-0">
                  {editingActivityId === a.id ? (
                    <ActivityForm
                      dealContacts={dealContacts}
                      initial={{ type: a.type, subject: a.subject, body: a.body || '', activityDate: a.activityDate ? a.activityDate.slice(0, 16) : '', contactIds: (a.contacts || []).map(c => c.id) }}
                      onSubmit={handleUpdateActivity}
                      onCancel={() => setEditingActivityId(null)}
                      submitLabel="Save"
                    />
                  ) : (
                    <div className="flex gap-3">
                      <span className={`mt-1 px-2 py-0.5 rounded text-xs font-medium shrink-0 ${
                        a.type === 'CALL' ? 'bg-blue-100 text-blue-800' : a.type === 'EMAIL' ? 'bg-purple-100 text-purple-800' : a.type === 'LINKEDIN_MESSAGE' ? 'bg-sky-100 text-sky-800' : 'bg-orange-100 text-orange-800'
                      }`}>{a.type}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-gray-900 font-medium">{a.subject}</p>
                        {a.contacts && a.contacts.length > 0 && (
                          <p className="text-sm text-gray-500 mt-0.5">
                            With {a.contacts.map((c, i) => (
                              <span key={c.id}>
                                {i > 0 && ', '}
                                <Link to={`/crm/contacts/${c.id}`} className="text-blue-600 hover:text-blue-800">{c.firstName} {c.lastName}</Link>
                              </span>
                            ))}
                          </p>
                        )}
                        {a.performedBy && <p className="text-sm text-gray-500 mt-0.5">By {userMap[a.performedBy] || a.performedBy}</p>}
                        {a.body && <p className="text-gray-600 text-sm whitespace-pre-wrap mt-1">{a.body}</p>}
                        <p className="text-xs text-gray-400 mt-1">{a.activityDate ? new Date(a.activityDate).toLocaleString() : new Date(a.createdAt).toLocaleString()}</p>
                      </div>
                      <button onClick={() => { setEditingActivityId(a.id); setShowActivityForm(false); }} className="text-gray-400 hover:text-blue-600 shrink-0 self-start" title="Edit">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No activity logged yet.</p>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Notes</h2>
        </div>
        <div className="p-4 md:p-6">
          <form onSubmit={handleAddNote} className="flex gap-2 mb-4">
            <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note..." rows={2} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button type="submit" disabled={!newNote.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 self-end">Add</button>
          </form>
          {deal.notes && deal.notes.length > 0 ? (
            <div className="space-y-4">
              {deal.notes.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((n) => (
                <div key={n.id} className="border-b pb-3 last:border-b-0">
                  <p className="text-gray-900 whitespace-pre-wrap">{n.body}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {n.createdBy && userMap[n.createdBy] && <span className="text-gray-500">{userMap[n.createdBy]} · </span>}
                    {new Date(n.createdAt).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No notes yet.</p>
          )}
        </div>
      </div>

      {/* Estimates */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Estimates</h2>
          <Link to={`/estimates/new?dealId=${id}`} className="text-sm text-blue-600 hover:text-blue-800">+ Create Estimate</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {estimates.map(e => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">
                    <Link to={`/estimates/${e.id}`} className="text-yellow-600 hover:text-yellow-800">{e.estimateNumber}</Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{e.title}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{e.total != null ? `$${e.total.toFixed(2)}` : '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      e.status === 'ACCEPTED' ? 'bg-green-100 text-green-800' :
                      e.status === 'SENT' ? 'bg-blue-100 text-blue-800' :
                      e.status === 'DECLINED' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>{e.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {estimates.length === 0 && <div className="text-center py-12 text-gray-500">No estimates for this deal.</div>}
        </div>
      </div>

      {/* Tasks */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Tasks</h2>
          <Link to={`/crm/tasks/new?dealId=${id}`} className="text-sm text-blue-600 hover:text-blue-800">+ Add task</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tasks.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">
                    <Link to={`/crm/tasks/${t.id}`} className="text-blue-600 hover:text-blue-800">{t.title}</Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{t.dueDate || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${PRIORITY_COLORS[t.priority] || ''}`}>{t.priority}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[t.status] || ''}`}>{t.status.replace(/_/g, ' ')}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {tasks.length === 0 && <div className="text-center py-12 text-gray-500">No tasks for this deal.</div>}
        </div>
      </div>

      <ConfirmModal open={deleteModal} title="Delete Deal" message="Are you sure you want to delete this deal?" onConfirm={handleDelete} onCancel={() => setDeleteModal(false)} />
    </Layout>
  );
}
